<template>
  <article class="input-image" v-bind="$attrs">
    <div class="label input__label">
      {{ labeltext || $t("words.image") }}
    </div>

    <input-image-zone
      :image-data="imageData"
      :field-id="fieldId"
      @delete="deleteImage"
      @change="setImage($event)"
    />

    <div v-if="image && controlObjectFit" class="list mt-4">
      <h4 class="label mv-0">{{ $t('labels.size') }}</h4>
      <!-- eslint-disable prettier/prettier -->
      <input-radio
        v-if="objectFitValues.includes('contain')"
        :value="image.objectFit"
        :data="('contain' as const)"
        :label="$t('labels.imageContain')"
        @update:value="setObjectFit"
      />
      <input-radio
        v-if="objectFitValues.includes('cover')"
        :value="image.objectFit"
        :data="('cover' as const)"
        :label="$t('labels.imageCover')"
        @update:value="setObjectFit"
      />
      <input-radio
        v-if="objectFitValues.includes('none')"
        :value="image.objectFit"
        :data="('none' as const)"
        :label="$t('labels.imageNoObjectFit')"
        @update:value="setObjectFit"
      />
      <!-- eslint-enable prettier/prettier -->
    </div>
    <div v-if="image && controlRepeat && image.objectFit != 'cover'" class="list mt-4">
      <h4 class="label mv-0">{{ $t('labels.imageRepetition') }}</h4>
      <input-radio
        :value="image.repeat"
        :data="('no-repeat' as const)"
        :label="$t('labels.imageNoRepeat')"
        @update:value="imageStore.updateKey(image.id, 'repeat', 'no-repeat')"
      />
      <input-radio
        :value="image.repeat"
        :data="('repeat' as const)"
        :label="$t('labels.imageRepeat')"
        @update:value="imageStore.updateKey(image.id, 'repeat', 'repeat')"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, useId } from "vue";

import InputRadio from "@core/input/RadioButton.vue";
import InputImageZone from "@core/input/InputImageZone.vue";

import { useImageStore } from "@editor/items/image/images.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { defineSyncEmits } from "@core/composables/defineSyncEmits";
import type { ImageItem } from "./ImageItem";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<{
  id?: number | null;
  labeltext?: string;
  maxWidth?: number;
  maxHeight?: number;
  compression?: number;
  controlObjectFit?: boolean;
  controlRepeat?: boolean;
  targetWidth?: number;
  targetHeight?: number;
  objectFitValues?: Array<"contain" | "cover" | "none">;
}>(), {
  maxWidth: 600,
  maxHeight: 600,
  compression: 0.7,
  showObjectFitControls: false,
  targetWidth: 270,
  targetHeight: 200,
  objectFitValues: () => ["contain", "cover"]
});
const emit = defineEmits<{
  (e: "delete"): void;
}>();

const emitSync = defineSyncEmits<{
  (e: "update", image: ImageItem): any;
}>();

const imageStore = useImageStore();

const fieldId = useId();

const image = computed(() => {
  if (props.id == null) return null;
  return imageStore.items[props.id];
});
const imageData = computed(() => {
  if (image.value == null) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

async function setImage(img: { src: string; width: number; height: number; type: string }) {
  if (props.id == null) {
    await createImage(img);
  } else {
    await updateImage(img);
  }
}

async function updateImage(image: { src: string; width: number; height: number }) {
  if (props.id == null) throw new Error("No image id set");

  useUndo().startGroup("model", "updateImage");

  const imageEntry = imageStore.update({
    id: props.id,
    height: image.height,
    width: image.width,
    data: image.src,
    aspectRatio: image.width / image.height
  });
  await emitSync("update", imageEntry);

  useUndo().endGroup("model");
  return imageEntry;
}

async function createImage(image: { src: string; width: number; height: number }) {
  useUndo().startGroup("model", "createImage");

  console.log("create image", image.height, image.width);
  const imageEntry = await imageStore.create({
    height: image.height,
    width: image.width,
    data: image.src,
    aspectRatio: image.width / image.height,
    objectFit: "contain"
  });
  await emitSync("update", imageEntry);

  useUndo().endGroup("model");
  return imageEntry;
}

function deleteImage() {
  if (props.id == null) return;
  imageStore.delete({ id: props.id });
  emit("delete");
}

function setObjectFit(value: "contain" | "cover" | "none") {
  console.log("set object fit", value);
  if (image.value == null) {
    console.warn("no image to set object fit");
    return;
  }
  return imageStore.update({
    id: image.value.id,
    objectFit: value
  });
}
</script>

<style lang="scss">
.input-image {
  position: relative;
}
</style>
